import { t } from "i18next";
import { useEffect, useState } from "react";
import {
  TranscriberClientEvents,
  TranscriberServerEvents,
} from "@/hooks/transcriber/constants";
import type {
  TranscriptCompletedMessage,
  TranscriptEndMessage,
  TranscriptPartialMessage,
} from "@/hooks/transcriber/types";
import { useSocketIoClient } from "@/hooks/transcriber/useSocketIoClient";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useTranscriptionStreamingStore } from "@/store/transcriptionStreaming.store";

const interrupted = (): string =>
  t("components.syncRecordingSheet.interrupted");
type SessionState = {
  roomId: string;
  transcription: TranscriptEndMessage | null;
  finished: boolean;
  isProviderReady: boolean;
  connectionError: string | null;
};

export const useSyncRecordingTranscription = ({
  roomId,
}: {
  roomId: string;
}) => {
  const { socket, routeError } = useSocketIoClient({ roomId });
  const initial: SessionState = {
    roomId,
    transcription: null,
    finished: false,
    isProviderReady: false,
    connectionError: null,
  };
  const [state, setState] = useState(initial);
  const { resetStreamText } = useTranscriptionStreamingStore.getState();

  useEffect(() => {
    let active = true;
    let terminal = false;
    const {
      setStreamText,
      setPartialText,
      settlePartialText,
      resetStreamText,
      finishSteamText,
    } = useTranscriptionStreamingStore.getState();
    const update = (values: Partial<typeof initial>) => {
      if (active) setState((previous) => ({ ...previous, ...values }));
    };
    const fail = () => {
      if (!active || terminal) return;
      terminal = true;
      finishSteamText();
      update({
        isProviderReady: false,
        connectionError: interrupted(),
        transcription: null,
      });
      socket.close();
    };
    setState({
      roomId,
      transcription: null,
      finished: false,
      isProviderReady: false,
      connectionError: null,
    });
    resetStreamText();
    const handlers = {
      [TranscriberServerEvents.PROVIDER_READY]: () => {
        if (!terminal) update({ isProviderReady: true });
      },
      [TranscriberServerEvents.PROVIDER_WAIT]: () => {
        if (!terminal) update({ isProviderReady: false });
      },
      // In-progress words from the phone's session: display only.
      [TranscriberServerEvents.TRANSCRIPT_PARTIAL]: (
        data: TranscriptPartialMessage,
      ) => {
        if (active && !terminal) setPartialText(data);
      },
      [TranscriberServerEvents.TRANSCRIPT_COMPLETED]: (
        data: TranscriptCompletedMessage,
      ) => {
        if (!active || terminal) return;
        settlePartialText(data.itemId);
        setStreamText({
          text: data.transcription,
          startTime: data.startTime,
        });
      },
      [TranscriberServerEvents.TRANSCRIPT_END]: (
        data: TranscriptEndMessage,
      ) => {
        if (!active || terminal) return;
        terminal = true;
        finishSteamText();
        update({ transcription: data, finished: true, isProviderReady: false });
      },
      [TranscriberServerEvents.PROVIDER_UNAVAILABLE]: fail,
      [TranscriberServerEvents.CONNECT_ERROR]: fail,
      error: fail,
      disconnect: fail,
    };
    for (const [event, handler] of Object.entries(handlers))
      socket.on(event, handler);
    const {
      inputLanguage,
      noiseReduction,
      transcriptionProvider,
      noiseSuppression,
      diarize,
    } = useRecordingSettingsStore.getState();
    const { userData } = useAuthStore.getState();
    socket.emit(TranscriberClientEvents.AUDIO_START, {
      inputLanguage,
      noiseReduction,
      userEmail: userData?.email,
      accountPublicId: userData?.public_id,
      transcriptionProvider,
      noiseSuppression,
      diarize,
      roomId,
    });
    return () => {
      active = false;
      for (const [event, handler] of Object.entries(handlers))
        socket.off(event, handler);
      socket.close();
    };
  }, [socket, roomId]);

  const visible = state.roomId === roomId ? state : initial;
  return {
    transcription: visible.transcription,
    finished: visible.finished,
    isProviderReady: visible.isProviderReady && !routeError,
    connectionError:
      visible.connectionError ??
      (routeError && !visible.finished ? interrupted() : null),
    finishSession: () => resetStreamText(),
    reset: () => {
      setState((previous) => ({ ...previous, finished: false }));
      resetStreamText();
    },
  };
};
